import React, { useState, useEffect, useCallback } from "react";
import { Button, ButtonGroup, Modal, Badge } from "react-bootstrap";
import {
  FaSync, FaChevronLeft, FaChevronRight,
  FaBuilding, FaCheckCircle, FaTimesCircle,
  FaExclamationTriangle, FaChartLine, FaUserPlus,
} from "react-icons/fa";
import {
  ComposedChart, Bar, Line, BarChart,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { toast } from "react-toastify";
import api from "../../services/api";

// ─── CORES ────────────────────────────────────────────────
const CORES_TIER = {
  BASIC:      "#6c757d",
  PRO:        "#4361ee",
  ENTERPRISE: "#7209b7",
};

const CORES_STATUS = {
  ativas:    "#4cc9f0",
  suspensas: "#f8961e",
  canceladas:"#f72585",
};

// ─── FORMATADORES ─────────────────────────────────────────
const formatarMoeda = (centavos) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format((centavos ?? 0) / 100);

const formatarData = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

// ─── SKELETONS ────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="stat-card">
    <div className="placeholder-glow">
      <span className="placeholder col-7 mb-2 d-block"
        style={{ height: 14, borderRadius: 4 }} />
      <span className="placeholder col-10 d-block"
        style={{ height: 32, borderRadius: 4 }} />
    </div>
  </div>
);

const SkeletonChart = () => (
  <div className="placeholder-glow p-3"
    style={{ height: 300, display: "flex", alignItems: "flex-end", gap: 12 }}>
    {[40, 70, 55, 90, 65, 80, 45, 75, 60, 85, 50, 95].map((h, i) => (
      <span key={i} className="placeholder flex-fill"
        style={{ height: `${h}%`, borderRadius: "4px 4px 0 0" }} />
    ))}
  </div>
);

const SkeletonTable = () => (
  <div className="placeholder-glow">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="d-flex gap-3 mb-3">
        <span className="placeholder col-3" style={{ height: 18, borderRadius: 4 }} />
        <span className="placeholder col-2" style={{ height: 18, borderRadius: 4 }} />
        <span className="placeholder col-2" style={{ height: 18, borderRadius: 4 }} />
        <span className="placeholder col-2" style={{ height: 18, borderRadius: 4 }} />
        <span className="placeholder col-2" style={{ height: 18, borderRadius: 4 }} />
      </div>
    ))}
  </div>
);

// ─── MODAL DETALHE ────────────────────────────────────────
const ModalDetalheEmpresa = ({ show, onClose, empresa }) => {
  if (!empresa) return null;

  const badgeMap = { ACTIVE: "success", SUSPENDED: "warning", CANCELED: "danger" };
  const labelMap = { ACTIVE: "Ativa",   SUSPENDED: "Suspensa", CANCELED: "Cancelada" };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaBuilding className="me-2 text-primary" />
          {empresa.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header fw-bold">Dados da Empresa</div>
              <div className="card-body">
                <p className="mb-1"><strong>E-mail:</strong> {empresa.email}</p>
                <p className="mb-1"><strong>Telefone:</strong> {empresa.phone ?? "-"}</p>
                <p className="mb-1">
                  <strong>Status: </strong>
                  <Badge bg={badgeMap[empresa.status]}>
                    {labelMap[empresa.status]}
                  </Badge>
                </p>
                <p className="mb-0">
                  <strong>Cadastro:</strong> {formatarData(empresa.createdAt)}
                </p>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header fw-bold">Assinatura</div>
              <div className="card-body">
                <p className="mb-1"><strong>Plano:</strong> {empresa.planName}</p>
                <p className="mb-1">
                  <strong>Tier: </strong>
                  <Badge style={{ backgroundColor: CORES_TIER[empresa.planTier] }}>
                    {empresa.planTier}
                  </Badge>
                </p>
                <p className="mb-1">
                  <strong>Valor:</strong> {formatarMoeda(empresa.planPrice)}/mês
                </p>
                <p className="mb-1">
                  <strong>Vencimento:</strong> {formatarData(empresa.cycleEndDate)}
                </p>
                <p className="mb-0">
                  <strong>Situação: </strong>
                  {empresa.diasRestantes === null ? (
                    <span className="text-muted">-</span>
                  ) : empresa.diasRestantes < 0 ? (
                    <span className="text-danger fw-bold">
                      {Math.abs(empresa.diasRestantes)}d em atraso
                    </span>
                  ) : empresa.diasRestantes <= 7 ? (
                    <span className="text-warning fw-bold">
                      Vence em {empresa.diasRestantes}d
                    </span>
                  ) : (
                    <span className="text-success">
                      {empresa.diasRestantes}d restantes
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-dark" onClick={onClose}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────
const SuperAdminDashboard = () => {
  const anoAtual = new Date().getFullYear();

  const [windowWidth, setWindowWidth]       = useState(window.innerWidth);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState("");
  const [periodoGrafico, setPeriodoGrafico] = useState(6);
  const [anoSelecionado, setAnoSelecionado] = useState(anoAtual);
  const [filtroTabela, setFiltroTabela]     = useState("todas");
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [showModal, setShowModal]           = useState(false);

  const [dashData, setDashData] = useState({
    cards: {
      totalEmpresas: 0, empresasAtivas: 0, empresasSuspensas: 0,
      empresasCanceladas: 0, novasUltimos30Dias: 0, mrr: 0, churnRate: 0,
    },
    charts: { mrrMensal: [], statusMensal: [], planDistribution: [] },
    table: { empresasCriticas: [] },
  });

  // ── Fetch ──────────────────────────────────────────────
  const fetchDados = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get("/superadmin/dashboard", {
        params: { meses: periodoGrafico, ano: anoSelecionado },
      });

      // Injeta cor nos planos para o PieChart
      const planDistribution = res.data.charts.planDistribution.map((p) => ({
        ...p,
        cor: CORES_TIER[p.tier] ?? "#6c757d",
      }));

      setDashData({
        ...res.data,
        charts: { ...res.data.charts, planDistribution },
      });
    } catch {
      toast.error("Erro ao carregar dashboard do superadmin");
      setError("Não foi possível carregar os indicadores.");
    } finally {
      setLoading(false);
    }
  }, [periodoGrafico, anoSelecionado]);

  useEffect(() => { fetchDados(); }, [fetchDados]);

  // Auto-refresh de 5min apenas no ano atual
  useEffect(() => {
    if (anoSelecionado !== anoAtual) return;
    const interval = setInterval(fetchDados, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDados, anoSelecionado, anoAtual]);

  useEffect(() => {
    const fn = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // ── Helpers ────────────────────────────────────────────
  const anoAnterior = () => setAnoSelecionado((p) => Math.max(p - 1, 2020));
  const anoProximo  = () => setAnoSelecionado((p) => Math.min(p + 1, anoAtual));

  const getGridCols = () => {
    if (windowWidth < 576) return 1;
    if (windowWidth < 992) return 2;
    return 4;
  };

  const abrirModal = (empresa) => {
    setEmpresaSelecionada(empresa);
    setShowModal(true);
  };

  // ── Filtros da tabela ──────────────────────────────────
  const empresasFiltradas = React.useMemo(() => {
    const lista = dashData.table.empresasCriticas ?? [];
    switch (filtroTabela) {
      case "suspensas": return lista.filter((e) => e.status === "SUSPENDED");
      case "vencendo":  return lista.filter((e) => e.diasRestantes >= 0 && e.diasRestantes <= 7);
      case "atrasadas": return lista.filter((e) => e.diasRestantes < 0);
      default:          return lista;
    }
  }, [dashData.table.empresasCriticas, filtroTabela]);

  const contadores = React.useMemo(() => {
    const lista = dashData.table.empresasCriticas ?? [];
    return {
      todas:     lista.length,
      suspensas: lista.filter((e) => e.status === "SUSPENDED").length,
      vencendo:  lista.filter((e) => e.diasRestantes >= 0 && e.diasRestantes <= 7).length,
      atrasadas: lista.filter((e) => e.diasRestantes < 0).length,
    };
  }, [dashData.table.empresasCriticas]);

  // ── Render ─────────────────────────────────────────────
  return (
    <div className="page-content">
      {error && <div className="alert alert-warning">{error}</div>}

      <ModalDetalheEmpresa
        show={showModal}
        onClose={() => setShowModal(false)}
        empresa={empresaSelecionada}
      />

      {/* ════════════════════════════════════
          HEADER
      ════════════════════════════════════ */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-bold">Dashboard SuperAdmin</h4>
          <small className="text-muted">Visão geral de todas as empresas</small>
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={fetchDados}
          disabled={loading}
        >
          <FaSync className={`me-1 ${loading ? "fa-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* ════════════════════════════════════
          1. CARDS KPI
      ════════════════════════════════════ */}
      <div
        className="stats-grid mb-3"
        style={{ gridTemplateColumns: `repeat(${getGridCols()}, 1fr)` }}
      >
        {loading
          ? [1, 2, 3, 4, 5, 6, 7].map((i) => <SkeletonCard key={i} />)
          : (
            <>
              <div className="stat-card">
                <div className="stat-title">
                  <FaBuilding className="me-1 text-secondary" /> Total de Empresas
                </div>
                <div className="stat-value">
                  {dashData.cards.totalEmpresas}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-title">
                  <FaCheckCircle className="me-1 text-success" /> Empresas Ativas
                </div>
                <div className="stat-value text-success">
                  {dashData.cards.empresasAtivas}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-title">
                  <FaExclamationTriangle className="me-1 text-warning" /> Suspensas
                </div>
                <div className="stat-value text-warning">
                  {dashData.cards.empresasSuspensas}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-title">
                  <FaTimesCircle className="me-1 text-danger" /> Canceladas
                </div>
                <div className="stat-value text-danger">
                  {dashData.cards.empresasCanceladas}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-title">
                  <FaChartLine className="me-1 text-primary" /> MRR Atual
                </div>
                <div className="stat-value">
                  {formatarMoeda(dashData.cards.mrr)}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-title">
                  <FaUserPlus className="me-1 text-info" /> Novas (30 dias)
                </div>
                <div className="stat-value text-info">
                  +{dashData.cards.novasUltimos30Dias}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-title">Churn Rate</div>
                <div
                  className="stat-value"
                  style={{
                    color: dashData.cards.churnRate > 5 ? "#f72585" : "#4cc9f0",
                  }}
                >
                  {dashData.cards.churnRate}%
                </div>
              </div>
            </>
          )}
      </div>

      {/* ════════════════════════════════════
          SELETOR PERÍODO + ANO
      ════════════════════════════════════ */}
      <div className="d-flex justify-content-between align-items-center mb-3 mt-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Período:</small>
          <ButtonGroup size="sm">
            {[3, 6, 12].map((m) => (
              <Button
                key={m}
                variant={periodoGrafico === m ? "primary" : "outline-primary"}
                onClick={() => setPeriodoGrafico(m)}
                disabled={loading}
              >
                {m} meses
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Ano:</small>
          <div className="d-flex align-items-center border rounded overflow-hidden">
            <Button
              variant="light" size="sm"
              onClick={anoAnterior}
              disabled={loading || anoSelecionado <= 2020}
              className="border-0 rounded-0 px-2"
            >
              <FaChevronLeft size={10} />
            </Button>
            <span
              className="px-3 py-1 fw-bold"
              style={{
                fontSize: 14,
                backgroundColor: anoSelecionado === anoAtual ? "#4361ee" : "#f8961e",
                color: "#fff",
                minWidth: 60,
                textAlign: "center",
              }}
            >
              {anoSelecionado}
            </span>
            <Button
              variant="light" size="sm"
              onClick={anoProximo}
              disabled={loading || anoSelecionado >= anoAtual}
              className="border-0 rounded-0 px-2"
            >
              <FaChevronRight size={10} />
            </Button>
          </div>
          {anoSelecionado !== anoAtual && (
            <Button
              variant="outline-secondary" size="sm"
              onClick={() => setAnoSelecionado(anoAtual)}
              disabled={loading}
            >
              Hoje
            </Button>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════
          2. GRÁFICOS — LINHA 1
      ════════════════════════════════════ */}
      <div className={windowWidth < 992 ? "" : "charts-grid"}>

        {/* MRR Mensal */}
        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title mb-0">Evolução do MRR</h5>
          </div>
          <div className="card-body">
            {loading ? <SkeletonChart /> : (
              dashData.charts.mrrMensal.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={dashData.charts.mrrMensal}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      yAxisId="esquerda"
                      stroke="#6b7280"
                      fontSize={12}
                      tickFormatter={(v) =>
                        `R$${((v / 100) / 1000).toFixed(0)}k`
                      }
                    />
                    <YAxis
                      yAxisId="direita"
                      orientation="right"
                      stroke="#6b7280"
                      fontSize={12}
                      allowDecimals={false}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "MRR"
                          ? formatarMoeda(value)
                          : [value, name]
                      }
                    />
                    <Legend />
                    <Bar
                      yAxisId="esquerda"
                      dataKey="mrr"
                      fill="#4361ee"
                      name="MRR"
                      radius={[2, 2, 0, 0]}
                    />
                    <Line
                      yAxisId="direita"
                      type="monotone"
                      dataKey="novasEmpresas"
                      stroke="#f72585"
                      strokeWidth={3}
                      name="Novas Empresas"
                      dot={{ r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  Nenhum dado encontrado.
                </div>
              )
            )}
          </div>
        </div>

        {/* Status Mensal */}
        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title mb-0">Empresas por Status</h5>
          </div>
          <div className="card-body">
            {loading ? <SkeletonChart /> : (
              dashData.charts.statusMensal.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dashData.charts.statusMensal}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis
                      stroke="#6b7280"
                      fontSize={12}
                      allowDecimals={false}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="ativas"     stackId="a" fill={CORES_STATUS.ativas}     name="Ativas" />
                    <Bar dataKey="suspensas"  stackId="a" fill={CORES_STATUS.suspensas}  name="Suspensas" />
                    <Bar dataKey="canceladas" stackId="a" fill={CORES_STATUS.canceladas} name="Canceladas"
                      radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  Nenhum dado encontrado.
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════
          3. PIE + TABELA
      ════════════════════════════════════ */}
      <div className={windowWidth < 992 ? "" : "charts-grid"}>

        {/* Distribuição por Plano */}
        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title mb-0">Distribuição por Plano</h5>
          </div>
          <div className="card-body">
            {loading ? <SkeletonChart /> : (
              dashData.charts.planDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" minHeight={300}>
                  <PieChart>
                    <Pie
                      data={dashData.charts.planDistribution}
                      cx="50%" cy="50%"
                      outerRadius="70%"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {dashData.charts.planDistribution.map((entry, i) => (
                        <Cell key={i} fill={entry.cor} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value} empresa(s) • ${formatarMoeda(props.payload.mrr)}`,
                        name,
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">
                  Nenhum plano ativo.
                </div>
              )
            )}
          </div>
        </div>

        {/* Tabela empresas críticas */}
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <FaExclamationTriangle className="me-2 text-warning" />
              Empresas em Atenção
            </h5>
            <Button
              variant="outline-primary" size="sm"
              onClick={fetchDados} disabled={loading}
            >
              <FaSync className={`me-1 ${loading ? "fa-spin" : ""}`} />
            </Button>
          </div>

          <div className="card-body">
            {/* Filtros */}
            <div className="mb-3">
              <ButtonGroup size="sm" className="w-100">
                <Button
                  variant={filtroTabela === "todas" ? "secondary" : "outline-secondary"}
                  onClick={() => setFiltroTabela("todas")}
                >
                  Todas ({contadores.todas})
                </Button>
                <Button
                  variant={filtroTabela === "suspensas" ? "warning" : "outline-warning"}
                  onClick={() => setFiltroTabela("suspensas")}
                >
                  Suspensas ({contadores.suspensas})
                </Button>
                <Button
                  variant={filtroTabela === "vencendo" ? "info" : "outline-info"}
                  onClick={() => setFiltroTabela("vencendo")}
                >
                  Vencendo ({contadores.vencendo})
                </Button>
                <Button
                  variant={filtroTabela === "atrasadas" ? "danger" : "outline-danger"}
                  onClick={() => setFiltroTabela("atrasadas")}
                >
                  Atrasadas ({contadores.atrasadas})
                </Button>
              </ButtonGroup>
            </div>

            <div className="table-responsive">
              {loading ? <SkeletonTable /> : (
                empresasFiltradas.length > 0 ? (
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Empresa</th>
                        <th>Plano</th>
                        <th>Status</th>
                        <th>Vencimento</th>
                        <th>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {empresasFiltradas.map((empresa) => {
                        const atrasada = empresa.diasRestantes !== null && empresa.diasRestantes < 0;
                        const vencendo = empresa.diasRestantes !== null && empresa.diasRestantes >= 0 && empresa.diasRestantes <= 7;

                        return (
                          <tr
                            key={empresa.id}
                            className={
                              atrasada ? "table-danger" :
                              vencendo ? "table-warning" : ""
                            }
                            style={{ cursor: "pointer" }}
                            onClick={() => abrirModal(empresa)}
                            title="Clique para ver detalhes"
                          >
                            <td>
                              <strong>{empresa.name}</strong>
                              <br />
                              <small className="text-muted">{empresa.email}</small>
                            </td>
                            <td>
                              <Badge
                                style={{
                                  backgroundColor: CORES_TIER[empresa.planTier] ?? "#6c757d",
                                }}
                              >
                                {empresa.planTier}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  empresa.status === "ACTIVE"    ? "success" :
                                  empresa.status === "SUSPENDED" ? "warning" : "danger"
                                }
                              >
                                {empresa.status === "ACTIVE"    ? "Ativa" :
                                 empresa.status === "SUSPENDED" ? "Suspensa" : "Cancelada"}
                              </Badge>
                            </td>
                            <td>
                              <small>{formatarData(empresa.cycleEndDate)}</small>
                            </td>
                            <td>
                              {empresa.diasRestantes === null ? (
                                <small className="text-muted">-</small>
                              ) : atrasada ? (
                                <small className="text-danger fw-bold">
                                  {Math.abs(empresa.diasRestantes)}d atraso
                                </small>
                              ) : vencendo ? (
                                <small className="text-warning fw-bold">
                                  {empresa.diasRestantes}d restantes
                                </small>
                              ) : (
                                <small className="text-success">
                                  {empresa.diasRestantes}d restantes
                                </small>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-4 text-muted">
                    Nenhuma empresa encontrada para este filtro.
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
